import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getDailySectionTitle, getDailySectionArticles, getDailySectionNavigation, testSantoDelDia, debugSantosPrioridad } from './utils/WeeklySchedule';
import { logSections, logNavigation, logAPI, devLog } from './utils/logger';
import UpdateNotification from './components/UpdateNotification';
import './App.css';

// Importar DataLoader para asegurar que se ejecute
import './utils/DataLoader';

// Importar servicio de Push Notifications
import pushNotificationService from './services/pushNotifications';

// Importar Status Bar y App de Capacitor
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';

// Importar componentes existentes
import Header from './Header';
import FullCalendar from './FullCalendar';
import ContentSection from './ContentSection';
import ArticleListView from './ArticleListView';
import ArticleDetailView from './ArticleDetailView';
import SocialMediaView from './SocialMediaView';
import VenYVerasView from './VenYVerasView';

// Importar nuevos componentes modernos
import LoadingScreen from './components/LoadingScreen';
import NavigationPills from './components/NavigationPills';
import BottomNavigation from './components/BottomNavigation';
import VideoCarousel from './components/VideoCarousel';
import AudioModal from './components/AudioModal';
import VideoModal from './components/VideoModal';
import ClaveAgustinianaView from './ClaveAgustinianaView';
import CaminoView from './CaminoView';
import CaminoHomeCard from './components/CaminoHomeCard';
import PullToRefresh from './components/PullToRefresh';
import OfflineNotification from './components/OfflineNotification';
import InstallAppBanner from './components/InstallAppBanner';
import FavoritesSection from './components/FavoritesSection';
import { useFavorites } from './hooks/useFavorites';
import { getConfigInicial, fetchRemoteConfig } from './utils/remoteConfig';
import { PLACEHOLDER_MINIATURA } from './utils/placeholders';
import TermsView from './TermsView';
import PrivacyView from './PrivacyView';
import ConfiguracionView from './ConfiguracionView';
import CreditosView from './CreditosView';
import NotificationsHistoryView from './NotificationsHistoryView';
import Footer from './Footer';

// Componente de carga actualizado para ApiDataLoader
function ApiTestDataLoader({ onDataLoaded }) {
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState({ current: 0, total: 100, articles: 0 });
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Conectando...');

  useEffect(() => {
    async function cargarDatosDesdeServidor() {
      try {
        // Intentar cargar desde caché primero (solo para apps nativas offline)
        const cachedSections = localStorage.getItem('agustinos_sections_light');
        const isOffline = !navigator.onLine;
        
        if (isOffline && cachedSections) {
          console.log('📴 Sin conexión, cargando desde caché ligero...');
          try {
            const sections = JSON.parse(cachedSections);
            setStatus('💾 Cargando contenido guardado...');
            setProgress({ current: 100, total: 100, articles: Object.values(sections).flat().length });
            
            onDataLoaded({
              articles: sections,
              loader: window.agustinosDataLoader,
              stats: { totalArticulos: Object.values(sections).flat().length },
              capabilities: { supportsPagination: false },
              source: 'cache-offline'
            });
            
            setLoading(false);
            return;
          } catch (e) {
            console.warn('Error parseando caché:', e);
          }
        }
        
        setStatus('🔗 Conectando con servidor...');
        logAPI('Iniciando carga desde servidor remoto...');
        
        // Verificar que el DataLoader esté disponible
        let loader = window.agustinosDataLoader;
        let attempts = 0;
        const maxAttempts = 20; // Aumentar intentos
        
        // Si no está disponible inmediatamente, esperar
        while (!loader && attempts < maxAttempts) {
          console.log(`⏳ Esperando DataLoader... intento ${attempts + 1}/${maxAttempts}`);
          await new Promise(resolve => setTimeout(resolve, 200)); // Reducir tiempo de espera
          loader = window.agustinosDataLoader;
          attempts++;
          
          // Debug: mostrar qué hay en window
          if (attempts === 5) {
            console.log('🔍 Verificando window.agustinosDataLoader:', typeof window.agustinosDataLoader);
            console.log('🔍 Keys en window que empiezan con "agust":', Object.keys(window).filter(k => k.includes('agust')));
          }
        }
        
        if (!loader) {
          console.error('❌ window.agustinosDataLoader no está disponible después de', maxAttempts, 'intentos');
          console.error('🔍 Contenido de window:', Object.keys(window).filter(k => k.includes('agust') || k.includes('Data')));
          throw new Error('ApiDataLoader no se pudo inicializar después de varios intentos');
        }
        
        // Verificar si hay error en el loader
        if (loader.error) {
          console.error('❌ ApiDataLoader tiene un error:', loader.message);
          throw new Error('Error en ApiDataLoader: ' + loader.message);
        }

        logAPI('DataLoader encontrado:', loader);

        // 🆕 Verificar si necesitamos refresh (última carga > 12 horas)
        const lastLoadTime = localStorage.getItem('agustinos_last_load_time');
        const now = Date.now();
        const shouldForceRefresh = !lastLoadTime || (now - parseInt(lastLoadTime)) > (12 * 60 * 60 * 1000);
        
        if (shouldForceRefresh) {
          console.log('🔄 Primera carga del día o pasaron >12h - Limpiando caché para contenido fresco');
          loader.limpiarCache('articles');
          localStorage.setItem('agustinos_last_load_time', now.toString());
        } else {
          console.log('✅ Carga reciente - Usando caché inteligente');
        }

        // Paso 1: Inicializar con funcionalidades mejoradas (Service Worker maneja caché automáticamente)
        setStatus('📚 Cargando artículos...');
        setProgress({ current: 30, total: 100, articles: 0 });
        
        const initResult = await loader.inicializar({ loadAll: true, forceRefresh: shouldForceRefresh });
        const { stats: serverStats, sections, pagination, capabilities, hasMore, totalArticles, source, isLimited } = initResult;
        
        setProgress({ current: 90, total: 100, articles: totalArticles });
        
        logAPI(`${totalArticles} artículos cargados en ${Object.keys(sections).length} secciones`);
        devLog('Capacidades detectadas:', capabilities);
        devLog('Origen de datos:', source, '- Limitado:', isLimited);

        // Paso 2: Finalizar
        const statusText = capabilities.isLimited 
          ? `⚠️ Cargados ${totalArticles} de ${capabilities.totalArticlesAvailable} artículos`
          : '🎉 ¡Todo el contenido cargado!';
          
        setStatus(statusText);
        setProgress({ current: 100, total: 100, articles: totalArticles });

        // Preparar datos mejorados para el componente principal
        const finalStats = {
          totalArticulos: totalArticles,
          conAudio: Object.values(sections).flat().filter(a => a.hasAudio).length,
          secciones: Object.keys(sections).length,
          fechaReciente: new Date(),
          fechaAntigua: new Date(2022, 3, 1),
          archivosRango: capabilities.supportsPagination ? 'Servidor moderno' : 'Servidor legacy (limitado)',
          ...serverStats
        };

        onDataLoaded({
          articles: sections,
          loader: loader,
          stats: finalStats,
          pagination: pagination,
          capabilities: capabilities,
          hasMore: hasMore,
          source: source || 'general',
          isLimited: isLimited !== false
        });

        // Guardar caché ligero en segundo plano (sin bloquear)
        setTimeout(() => {
          try {
            const lightCache = {};
            Object.keys(sections).forEach(sectionName => {
              lightCache[sectionName] = (sections[sectionName] || []).map(art => ({
                id: art.id,
                title: art.title,
                description: art.description?.substring(0, 150),
                date: art.date,
                section: art.section,
                hasAudio: art.hasAudio,
                hasVideo: art.hasVideo,
                imageUrl: art.imageUrl || art.image
              }));
            });
            localStorage.setItem('agustinos_sections_light', JSON.stringify(lightCache));
            console.log('💾 Caché ligero guardado para uso offline');
          } catch (e) {
            console.warn('No se pudo guardar caché ligero:', e);
          }
        }, 2000); // Esperar 2 segundos después de cargar

        setLoading(false);

      } catch (err) {
        console.error('❌ Error cargando datos desde servidor:', err);
        setError(err.message);
        setLoading(false);
      }
    }

    cargarDatosDesdeServidor();
  }, [onDataLoaded]);

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '20px',
        textAlign: 'center',
        background: '#f8f9fa'
      }}>
        <div style={{
          maxWidth: '400px',
          padding: '30px',
          background: 'white',
          borderRadius: '20px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>📡</div>
          <h3 style={{ 
            fontSize: '20px', 
            fontWeight: 'bold', 
            color: '#2d3748',
            marginBottom: '10px'
          }}>
            Sin conexión a internet
          </h3>
          <p style={{ 
            fontSize: '14px', 
            color: '#718096',
            lineHeight: '1.6',
            marginBottom: '20px'
          }}>
            No se pudo conectar con el servidor y no hay contenido guardado disponible.
          </p>
          <p style={{ 
            fontSize: '13px', 
            color: '#a0aec0',
            marginBottom: '25px',
            padding: '12px',
            background: '#f7fafc',
            borderRadius: '8px'
          }}>
            💡 Abre la app cuando tengas conexión para descargar el contenido.
          </p>
          <button 
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #e62c19 0%, #a01916 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              boxShadow: '0 4px 15px rgba(230, 44, 25, 0.3)',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(230, 44, 25, 0.4)';
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 15px rgba(230, 44, 25, 0.3)';
            }}
          >
            🔄 Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingScreen progress={progress} status={status} />;
  }

  return null;
}

// El santoral completo solo hace falta traerlo una vez por sesión. La marca vive
// fuera del componente porque navegar entre rutas remonta AppContent.
let santoralCompletoCargado = false;

// Componente interno que maneja la lógica de la app
function AppContent({
  initialView = 'main',
  initialArticleId = null,
  initialSection = '',
  initialCaminoTab = 'etapas',
  initialListFilter = null,
  onNavigateBack = null,
  // ===== NUEVOS PROPS (recibidos de App.js) =====
  allArticles,
  dataLoader,
  stats,
  orderedSections,
  sectionsLoading,
  onUpdateArticles
}) {
  const navigate = useNavigate();
  // const location = useLocation(); // Comentado para evitar warning

  // ---- TUS ESTADOS EXISTENTES ----
  const [currentView, setCurrentView] = useState(initialView);
  const [currentSection, setCurrentSection] = useState(initialSection);
  const [currentArticle, setCurrentArticle] = useState(null);
  // Configuración remota. Arranca con el cache para que la portada no cambie a
  // media carga, y se refresca contra el fichero en segundo plano.
  const [remoteConfig, setRemoteConfig] = useState(getConfigInicial);

  useEffect(() => {
    let vivo = true;
    fetchRemoteConfig().then(cfg => { if (vivo) setRemoteConfig(cfg); });
    return () => { vivo = false; };
  }, []);

  // Filtro preseleccionado al abrir una sección en modo lista. Lo usa el pill de
  // Santos agustinos para entrar directamente en el mes en curso. Llega por URL
  // (/seccion/santos-agustinos?filtro=mes-8), porque navegar remonta este
  // componente a través de SectionRoute.
  const [listInitialFilter, setListInitialFilter] = useState(initialListFilter);
  const [showCalendar, setShowCalendar] = useState(false);
  // Tab inicial al abrir la vista del Camino. Puede venir como prop (al entrar
  // por URL /camino?tab=galeria) o setearse en runtime desde la card de Home.
  const [caminoInitialTab, setCaminoInitialTab] = useState(initialCaminoTab);
  const [audioModal, setAudioModal] = useState({
    isOpen: false,
    article: null,
    sectionTitle: ''
  });
  const [videoModal, setVideoModal] = useState({
    isOpen: false,
    article: null,
    sectionTitle: ''
  });
  // Variable global para debouncing (fuera del componente)
  if (!window.agustinosLastUpdate) {
    window.agustinosLastUpdate = 0;
  }

  const { favorites } = useFavorites();

  const favoriteArticles = React.useMemo(() => {
    if (!favorites || favorites.length === 0 || !allArticles) return [];
    const allFlat = Object.values(allArticles).flat();
    const resolved = favorites
      .map(id => allFlat.find(a => String(a.id) === String(id)))
      .filter(Boolean);
    return resolved.reverse();
  }, [favorites, allArticles]);

  // Sincronizar favoritos en allArticles cuando se está en la sección
  useEffect(() => {
    if (currentSection === 'Mis favoritos' && favoriteArticles.length > 0) {
      const current = allArticles['Mis favoritos'] || [];
      if (current.length !== favoriteArticles.length) {
        onUpdateArticles({
          ...allArticles,
          'Mis favoritos': favoriteArticles
        });
      }
    }
  }, [currentSection, favoriteArticles]); // eslint-disable-line react-hooks/exhaustive-deps

  // ===== BOTÓN ATRÁS DE ANDROID =====
  useEffect(() => {
    if (Capacitor.getPlatform() !== 'android') return;

    const listener = CapApp.addListener('backButton', ({ canGoBack }) => {
      if (currentView === 'main') {
        CapApp.minimizeApp();
      } else {
        handleBackClick();
      }
    });

    return () => {
      listener.then(l => l.remove());
    };
  }, [currentView]); // eslint-disable-line react-hooks/exhaustive-deps

  // ===== SCROLL AL TOP AL CAMBIAR DE VISTA =====
  useEffect(() => {
    // Hacer scroll al top cuando cambiamos de vista o artículo
    // Esto asegura que siempre empezamos desde arriba al navegar
    window.scrollTo(0, 0);
  }, [currentView, currentArticle]); // Escucha cambios en vista y artículo

// Manejar secciones múltiples al inicializar
const hasInitialized = useRef(false);

useEffect(() => {
  if (initialSection === 'Historia y personajes + Sabías que…' && 
      !hasInitialized.current &&
      Object.keys(allArticles).length > 0) {
    
    console.log('🔄 Detectada sección múltiple al inicializar, combinando artículos...');
    
    const secciones = ['Historia y personajes', 'Sabías que…'];
    const articulosCombinados = [];
    
    secciones.forEach(seccion => {
      const articulosSeccion = allArticles[seccion] || [];
      articulosCombinados.push(...articulosSeccion);
    });
    
    // Ordenar por fecha (más recientes primero)
    articulosCombinados.sort((a, b) => new Date(b.date) - new Date(a.date));
    
    // Guardar los artículos combinados (actualizar estado global)
    onUpdateArticles({
      ...allArticles,
      'Historia y personajes + Sabías que…': articulosCombinados
    });
    
    hasInitialized.current = true;
    console.log(`✅ Sección múltiple inicializada con ${articulosCombinados.length} artículos`);
  }
}, [initialSection, allArticles, onUpdateArticles]); // eslint-disable-line react-hooks/exhaustive-deps

// ---- FUNCIONES EXISTENTES MEJORADAS ----
const handleSectionClick = async (section, options = {}) => {
  console.log(`📂 Navegando a sección: "${section}"`);
  
  // Manejar sección especial "Mis favoritos"
  if (section === 'Mis favoritos') {
    console.log(`⭐️ Creando sección virtual de favoritos`);
    onUpdateArticles({
      ...allArticles,
      'Mis favoritos': favoriteArticles
    });

    setCurrentSection('Mis favoritos');
    setCurrentView('list');

    if (navigate) {
      navigate('/seccion/mis-favoritos');
    }
    return;
  }

  // Manejar sección especial "Vídeos destacados" - mostrar TODOS los videos
  if (section === 'Vídeos destacados') {
    console.log(`🎥 Creando sección virtual de todos los vídeos`);
    const buenosDiasArticles = allArticles['Buenos días'] || [];
    const todosLosVideos = buenosDiasArticles
      .filter(article => article.hasVideo && article.videoUrl)
      .sort((a, b) => new Date(b.date) - new Date(a.date)); // Más recientes primero (sin límite)
    
    // Guardar los vídeos en una sección virtual
    onUpdateArticles({
      ...allArticles,
      'Vídeos destacados': todosLosVideos
    });
    
    setCurrentSection('Vídeos destacados');
    setCurrentView('list');
    
    if (navigate) {
      navigate('/seccion/videos-destacados');
    }
    return;
  }
  
  // Manejar arrays de secciones (ej: Historia y personajes + Sabías qué)
  if (Array.isArray(section)) {
    console.log(`📂 Manejando múltiples secciones:`, section);
    // Combinar artículos de múltiples secciones
    const articulosCombinados = [];
    const nombreSeccionCombinada = section.join(' + ');
    
    section.forEach(seccionIndividual => {
      const articulosSeccion = allArticles[seccionIndividual] || [];
      articulosCombinados.push(...articulosSeccion);
    });
    
    // Ordenar por fecha (más recientes primero)
    articulosCombinados.sort((a, b) => new Date(b.date) - new Date(a.date));
    
    // Guardar los artículos combinados (actualizar estado global)
    onUpdateArticles({
      ...allArticles,
      [nombreSeccionCombinada]: articulosCombinados
    });
    
    setCurrentSection(nombreSeccionCombinada);
    setCurrentView('list');
    
    // Para URLs, usar la primera sección del array
    if (navigate) {
      const sectionSlug = 'historia-y-sabias-que';
      navigate(`/seccion/${sectionSlug}`);
    }
    return;
  }
  
  // Los filtros por mes de Santos Agustinos se construyen sobre los artículos
  // que ya están en memoria, y la carga inicial de la app está limitada. Si se
  // pide un mes concreto hay que traer la sección completa antes de entrar, o el
  // mes puede aparecer vacío.
  if (section === 'Santos Agustinos' && options.filter && !santoralCompletoCargado) {
    const santosEnMemoria = allArticles['Santos Agustinos'] || [];
    const loader = window.agustinosAppData?.loader;
    if (loader?.cargarSeccionCompleta) {
      try {
        const santosCompletos = await loader.cargarSeccionCompleta('Santos Agustinos', 500);
        santoralCompletoCargado = true;
        console.log(`👼 Santoral completo: ${santosCompletos.length} fichas (antes ${santosEnMemoria.length})`);
        if (santosCompletos.length > santosEnMemoria.length) {
          onUpdateArticles({
            ...allArticles,
            'Santos Agustinos': santosCompletos
          });
        }
      } catch (error) {
        console.warn('⚠️ No se pudo cargar el santoral completo:', error);
      }
    }
  }

  setListInitialFilter(options.filter || null);

  // Navegación simple sin lazy loading
  setCurrentSection(section);
  setCurrentView('list');
  
  // Actualizar URL
  if (navigate) {
    const sectionSlug = createSlug(section);
    let url = `/seccion/${sectionSlug}`;
    const params = [];
    
    // Agregar parámetro de origen si viene de Clave agustiniana
    if (options.source === 'clave-agustiniana') {
      params.push('from=clave-agustiniana');
    }
    
    // El filtro viaja en la URL porque navegar remonta AppContent a través de
    // SectionRoute y el estado local se perdería.
    if (options.filter) {
      params.push(`filtro=${encodeURIComponent(options.filter)}`);
    }
    
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    
    navigate(url);
    console.log(`🌐 URL actualizada: ${url}`);
  }
};

  // ===== NOTA: handleDataLoaded ahora está en App.js (estado global) =====
  
  // FUNCIONES MEJORADAS PARA APILOADER - DEFINIR ANTES DE USAR:
  // Función para crear slug SEO desde título
  function createSlug(title) {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Quitar acentos
      .replace(/[^a-z0-9\s-]/g, '') // Solo letras, números, espacios y guiones
      .replace(/\s+/g, '-') // Espacios a guiones
      .replace(/-+/g, '-') // Múltiples guiones a uno
      .replace(/^-|-$/g, ''); // Quitar guiones del inicio/final
  }

  // Función para cargar artículo por ID
  const loadArticleById = async (articleId) => {
    if (!dataLoader) {
      console.error('❌ DataLoader no disponible para cargar artículo');
      return;
    }
    
    try {
      console.log(`🔍 Cargando artículo ID: ${articleId}`);
      const article = await dataLoader.obtenerArticulo(articleId);
      
      if (article) {
        console.log(`✅ Artículo cargado: ${article.title}`);
        setCurrentArticle(article);
        setCurrentView('detail');
        
        // Usar la sección del artículo del backend (prioridad sobre URL)
        const realSection = article.section || initialSection;
        console.log(`🔍 Estableciendo sección: "${realSection}" (article.section: "${article.section}", initialSection: "${initialSection}")`);
        setCurrentSection(realSection);
      } else {
        console.error(`❌ Artículo ID ${articleId} no encontrado`);
        // Intentar buscar en cache local
        const cachedArticle = window.obtenerArticuloPorId?.(articleId);
        if (cachedArticle) {
          console.log(`💾 Artículo encontrado en cache: ${cachedArticle.title}`);
          setCurrentArticle(cachedArticle);
          setCurrentView('detail');
          // También establecer sección desde cache
          const realSection = cachedArticle.section || initialSection;
          console.log(`🔍 Estableciendo sección desde cache: "${realSection}"`);
          setCurrentSection(realSection);
        } else {
          console.error(`❌ Artículo ID ${articleId} no existe en cache tampoco`);
        }
      }
    } catch (error) {
      console.error('❌ Error cargando artículo:', error);
      // Fallback: buscar en cache local
      const cachedArticle = window.obtenerArticuloPorId?.(articleId);
      if (cachedArticle) {
        console.log(`💾 Fallback: usando artículo desde cache: ${cachedArticle.title}`);
        setCurrentArticle(cachedArticle);
        setCurrentView('detail');
        // También establecer sección desde cache en fallback
        const realSection = cachedArticle.section || initialSection;
        console.log(`🔍 Estableciendo sección desde fallback: "${realSection}"`);
        setCurrentSection(realSection);
      }
    }
  };

  // Función para navegar a un artículo (con URL)
  const navigateToArticle = (article, section) => {
    logNavigation(`Navegando a artículo: ${article.title}`);
    
    setCurrentArticle(article);
    setCurrentView('detail'); // Usar 'detail' que es lo que espera el renderizado
    
    // Actualizar URL
    if (navigate) {
      const sectionSlug = section.toLowerCase().replace(/\s+/g, '-');
      const articleSlug = createSlug(article.title);
      const url = `/${sectionSlug}/${article.id}/${articleSlug}`;
      navigate(url);
      logNavigation(`URL actualizada: ${url}`);
    }
  };

  // Efecto para cargar artículo inicial cuando dataLoader esté listo
  useEffect(() => {
    if (dataLoader && initialArticleId) {
      logNavigation(`DataLoader listo - Cargando artículo inicial: ${initialArticleId}`);
      loadArticleById(initialArticleId);
    }
  }, [dataLoader, initialArticleId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Efecto para escuchar eventos de santos desde el calendario
  useEffect(() => {
    const handleSantoArticleOpen = (event) => {
      const { article, section } = event.detail;
      console.log(`🎯 Evento recibido para abrir artículo de santo: ${article.title}`);
      
      // Navegar al artículo del santo
      setCurrentArticle(article);
      setCurrentSection(section);
      setCurrentView('detail');
      
      // Actualizar URL si hay navegación disponible
      if (navigate) {
        const sectionSlug = createSlug(section);
        const articleSlug = createSlug(article.title);
        const url = `/${sectionSlug}/${article.id}/${articleSlug}`;
        navigate(url);
        console.log(`🌐 URL actualizada para santo: ${url}`);
      }
    };

    const handleOpenCalendarPopup = () => {
      console.log('🗓️ Evento recibido para abrir calendario popup');
      setShowCalendar(true);
    };

    window.addEventListener('openSantoArticle', handleSantoArticleOpen);
    const handleVirtualSectionNavigation = (event) => {
      const { section } = event.detail;
      console.log(`🎯 Evento recibido para sección virtual: ${section}`);
      
      // Evitar bucles: solo procesar si no es la sección actual
      if (currentSection !== section || currentView !== 'list') {
        handleSectionClick(section);
        setCurrentView('list');
      } else {
        console.log(`✅ Ya estamos en la sección "${section}" con vista lista`);
      }
    };
    
    const handleNavigateToClaveAgustiniana = () => {
      console.log('🔑 Evento recibido para navegar a Clave Agustiniana');
      // Ejecutar directamente la lógica sin llamar a la función externa
      setCurrentView('clave-agustiniana');
      if (navigate) {
        navigate('/clave-agustiniana');
      }
    };
    
    window.addEventListener('navigateToVirtualSection', handleVirtualSectionNavigation);
    window.addEventListener('navigateToClaveAgustiniana', handleNavigateToClaveAgustiniana);
    window.addEventListener('openCalendarPopup', handleOpenCalendarPopup);

    const handleOpenArticleFromAudio = (event) => {
      const { article, sectionTitle } = event.detail;
      console.log(`📖 Navegando a artículo desde modal de audio: ${article.title}`);
      console.log(`🔍 Datos del artículo:`, article);
      console.log(`🔍 Sección:`, sectionTitle);
      
      // Cerrar el modal primero
      setAudioModal({
        isOpen: false,
        article: null,
        sectionTitle: ''
      });
      
      // Navegar directamente usando la misma lógica que funciona en las tarjetas
      setCurrentArticle(article);
      setCurrentSection(sectionTitle);
      setCurrentView('detail');
      
      // Actualizar URL si hay navegación disponible
      if (navigate) {
        const sectionSlug = createSlug(sectionTitle);
        const articleSlug = createSlug(article.title);
        const url = `/${sectionSlug}/${article.id}/${articleSlug}`;
        navigate(url);
        console.log(`🌐 URL actualizada desde audio modal: ${url}`);
      }
    };

    window.addEventListener('openArticleFromAudio', handleOpenArticleFromAudio);

    const handleOpenArticleFromVideo = (event) => {
      const { article, sectionTitle } = event.detail;
      console.log(`📖 Navegando a artículo desde modal de video: ${article.title}`);
      console.log(`🔍 Datos del artículo:`, article);
      console.log(`🔍 Sección:`, sectionTitle);
      
      // Cerrar el modal primero
      setVideoModal({
        isOpen: false,
        article: null,
        sectionTitle: ''
      });
      
      // Navegar directamente usando la misma lógica que funciona en las tarjetas
      setCurrentArticle(article);
      setCurrentSection(sectionTitle);
      setCurrentView('detail');
      
      // Actualizar URL si hay navegación disponible
      if (navigate) {
        const sectionSlug = sectionTitle.toLowerCase().replace(/\s+/g, '-');
        const articleSlug = article.title
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '') // Quitar acentos
          .replace(/[^a-z0-9\s-]/g, '') // Solo letras, números, espacios y guiones
          .replace(/\s+/g, '-') // Espacios a guiones
          .replace(/-+/g, '-') // Múltiples guiones a uno
          .replace(/^-|-$/g, ''); // Quitar guiones del inicio/final
        const url = `/${sectionSlug}/${article.id}/${articleSlug}`;
        navigate(url);
        console.log(`🌐 URL actualizada desde video modal: ${url}`);
      }
    };

    window.addEventListener('openArticleFromVideo', handleOpenArticleFromVideo);

    // Manejar navegación desde búsqueda en VenYVerasView
    const handleNavigateToArticle = (event) => {
      const { article, section } = event.detail;
      console.log(`📎 Evento de navegación recibido: ${article.title} (${section})`);
      
      // Navegar al artículo
      setCurrentArticle(article);
      setCurrentSection(section);
      setCurrentView('detail');
      
      // Actualizar URL
      if (navigate) {
        const sectionSlug = createSlug(section);
        const articleSlug = createSlug(article.title);
        const url = `/${sectionSlug}/${article.id}/${articleSlug}`;
        navigate(url);
        console.log(`🌐 URL actualizada desde evento: ${url}`);
      }
    };

    window.addEventListener('navigateToArticle', handleNavigateToArticle);
    
    // Cleanup
    return () => {
      window.removeEventListener('openSantoArticle', handleSantoArticleOpen);
      window.removeEventListener('navigateToVirtualSection', handleVirtualSectionNavigation);
      window.removeEventListener('navigateToClaveAgustiniana', handleNavigateToClaveAgustiniana);
      window.removeEventListener('openCalendarPopup', handleOpenCalendarPopup);
      window.removeEventListener('openArticleFromAudio', handleOpenArticleFromAudio);
      window.removeEventListener('openArticleFromVideo', handleOpenArticleFromVideo);
      window.removeEventListener('navigateToArticle', handleNavigateToArticle);
    };
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps


  // ===== NOTA: generateOrderedSections ahora está en App.js (estado global) =====
  // ===== NOTA: orderedSections y sectionsLoading vienen como props =====
  // ===== NOTA: appReady check está en App.js - aquí la app SIEMPRE está lista =====

  // Función para cargar más artículos (disponible para uso futuro)
  // eslint-disable-next-line no-unused-vars
  const cargarMasArticulos = async (seccion, limite = 50) => {
    if (!dataLoader) return;
    
    try {
      console.log(`🔄 Intentando cargar más artículos de "${seccion}"...`);
      
      if (stats?.capabilities?.supportsPagination) {
        // Backend soporta paginación - cargar más datos reales
        const nuevosArticulos = await dataLoader.cargarMasArticulos(seccion, { limit: limite });
        
        if (nuevosArticulos.length > 0) {
          // Actualizar estado global con nuevos artículos
          onUpdateArticles({
            ...allArticles,
            [seccion]: [...(allArticles[seccion] || []), ...nuevosArticulos]
          });
          
          console.log(`✅ Cargados ${nuevosArticulos.length} artículos adicionales de "${seccion}"`);
        } else {
          console.log(`ℹ️ No hay más artículos disponibles en "${seccion}"`);
        }
      } else {
        // Backend legacy - ya tenemos todos los artículos disponibles
        const articulosSeccion = allArticles[seccion] || [];
        console.log(`📁 API legacy: Mostrando ${articulosSeccion.length} artículos de "${seccion}" (limitado por backend)`);
      }
      
    } catch (error) {
      console.error(`❌ Error cargando más artículos de "${seccion}":`, error);
    }
  };





  // Función de búsqueda mejorada
  window.buscarArticulos = async (termino, opciones = {}) => {
    if (!dataLoader || !termino) return [];
    
    try {
      console.log(`🔍 Buscando "${termino}"...`);
      
      const resultados = await dataLoader.buscarArticulos(termino, opciones);
      
      if (resultados.isLocal) {
        console.log(`🔍 Búsqueda local: ${resultados.totalResults} resultados para "${termino}"`);
        if (resultados.isLimited) {
          console.log(`⚠️ Resultados limitados - considera usar backend con paginación para búsquedas completas`);
        }
      } else {
        console.log(`🌐 Búsqueda en servidor: ${resultados.totalResults} resultados para "${termino}"`);
      }
      
      return resultados;
      
    } catch (error) {
      console.error(`❌ Error en búsqueda para "${termino}":`, error);
      return [];
    }
  };

  // Función para obtener artículo por ID actualizada
  window.obtenerArticuloPorId = (id) => {
    if (allArticles) {
      const todosLosArticulos = Object.values(allArticles).flat();
      const articulo = todosLosArticulos.find(a => a.id === id || a.id === parseInt(id));
      return articulo || null;
    }
    return null;
  };
    


  const handleArticleClick = (article, section = 'general') => {
    // Usar la función de navegación con URL
    navigateToArticle(article, section);
  };

  const handleBackClick = () => {
    // Detectar si venimos de Clave agustiniana
    const urlParams = new URLSearchParams(window.location.search);
    const fromClaveAgustiniana = urlParams.get('from') === 'clave-agustiniana';
    
    // Manejo específico para navegación desde Clave agustiniana hacia inicio
    if (currentView === 'clave-agustiniana') {
      console.log(`⬅️ Volviendo al inicio desde Clave agustiniana`);
      setCurrentView('main');
      setCurrentSection('');
      setCurrentArticle(null);
      if (navigate) {
        navigate('/');
      }
      return;
    }

    // Volver al inicio desde Camino de Santiago
    if (currentView === 'camino') {
      console.log(`⬅️ Volviendo al inicio desde Camino de Santiago`);
      setCurrentView('main');
      setCurrentSection('');
      setCurrentArticle(null);
      if (navigate) {
        navigate('/');
      }
      return;
    }

    // Opción 1: Usar historial del navegador (más natural)
    if (window.history.length > 1 && !fromClaveAgustiniana) {
      console.log(`⬅️ Usando historial del navegador`);
      window.history.back();
      return;
    }

    // Manejo especial para navegación desde Clave agustiniana
    if (currentView === 'list' && fromClaveAgustiniana) {
      console.log(`⬅️ Volviendo a Clave agustiniana desde sección`);
      setCurrentView('clave-agustiniana');
      if (navigate) {
        navigate('/clave-agustiniana');
      }
      return;
    }
    
    // Opción 2: Fallback manual si no hay historial
    if (currentView === 'detail') {
      // Si vienes de un artículo, detectar de dónde vino
      if (currentArticle && currentArticle.section) {
        // Volver a la sección del artículo
        console.log(`⬅️ Volviendo a sección: ${currentArticle.section}`);
        setCurrentSection(currentArticle.section);
        setCurrentView('list');
        
        // Actualizar URL manteniendo el parámetro from si existe
        if (navigate) {
          const sectionSlug = createSlug(currentArticle.section);
          let url = `/seccion/${sectionSlug}`;
          if (fromClaveAgustiniana) {
            url += '?from=clave-agustiniana';
          }
          navigate(url);
        }
      } else {
        // Si no hay sección, volver al inicio
        console.log(`⬅️ Volviendo al inicio (sin sección detectada)`);
        setCurrentView('main');
        if (navigate) {
          navigate('/');
        }
      }
    } else {
      // Desde cualquier otra vista, usar historial o volver al inicio
      if (window.history.length > 1) {
        window.history.back();
      } else {
        console.log(`⬅️ Volviendo al inicio`);
        setCurrentView('main');
        if (navigate) {
          navigate('/');
        }
      }
    }
  };

  const handleSocialMediaClick = () => {
    console.log('📱 Navegando a Media desde BottomNavigation');
    setCurrentView('social');
    
    // Actualizar URL (podrías crear una ruta específica como /media)
    if (navigate) {
      navigate('/media');
    }
  };

  const handleHomeClick = () => {
    console.log('🏠 Navegando al inicio desde BottomNavigation');
    setCurrentView('main');
    setCurrentSection('');
    setCurrentArticle(null);
    
    // Actualizar URL
    if (navigate) {
      navigate('/');
    }
  };

  const handleClaveAgustinianaClick = () => {
    console.log('🔑 Navegando a Clave agustiniana');
    setCurrentView('clave-agustiniana');
    
    // Actualizar URL
    if (navigate) {
      navigate('/clave-agustiniana');
    }
  };

  const handleRefresh = async () => {
    console.log('🔄 Pull-to-refresh activado - Refrescando contenido...');
    
    if (!dataLoader) {
      console.log('⚠️ DataLoader no disponible para refresh');
      return;
    }
    
    try {
      // Limpiar caché de artículos
      dataLoader.limpiarCache('articles');
      
      // Recargar datos desde servidor
      const freshData = await dataLoader.agruparPorSecciones({ forceRefresh: true });
      
      if (freshData && freshData.sections) {
        console.log('✅ Contenido refrescado exitosamente');
        
        // Actualizar estado global en App.js
        onUpdateArticles(freshData.sections);
      }
    } catch (error) {
      console.error('❌ Error al refrescar contenido:', error);
    }
  };

  const handleAudioClick = (article, sectionTitle) => {
    console.log(`🎵 Abriendo modal de audio para: ${article.title}`);
    setAudioModal({
      isOpen: true,
      article: article,
      sectionTitle: sectionTitle
    });
  };

  const handleCloseAudioModal = () => {
    console.log('🎵 Cerrando modal de audio');
    setAudioModal({
      isOpen: false,
      article: null,
      sectionTitle: ''
    });
  };

  const handleVideoModalClick = (article, sectionTitle) => {
    console.log(`🎥 Abriendo modal de video para: ${article.title}`);
    setVideoModal({
      isOpen: true,
      article: article,
      sectionTitle: sectionTitle
    });
  };

  const handleCloseVideoModal = () => {
    console.log('🎥 Cerrando modal de video');
    setVideoModal({
      isOpen: false,
      article: null,
      sectionTitle: ''
    });
  };

  // ---- RENDER ----
  return (
    <div className="app-container">
      <div className="content-container">
        <UpdateNotification />
        <OfflineNotification />
        <InstallAppBanner />
        {/* Debug info mejorado - SOLO visible con ?debug en URL */}
        {process.env.NODE_ENV === 'development' && 
         window.location.hostname === 'localhost' && 
         window.location.search.includes('debug') && 
         stats && dataLoader && (
          <div style={{
            position: 'fixed',
            top: '10px',
            right: '10px',
            background: 'rgba(0,0,0,0.9)',
            color: 'white',
            padding: '12px',
            borderRadius: '8px',
            fontSize: '11px',
            zIndex: 1000,
            maxWidth: '280px',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            <div style={{fontWeight: 'bold', marginBottom: '5px'}}>🔧 Debug Info</div>
            <div>📊 {stats.totalArticulos} artículos cargados</div>
            {stats.capabilities && (
              <div style={{marginTop: '5px'}}>
                {stats.capabilities.isLimited && (
                  <div style={{color: '#ffcc00'}}>⚠️ Limitado: {stats.capabilities.maxArticlesShown} de {stats.capabilities.totalArticlesAvailable}</div>
                )}
                <div>🔧 Paginación: {stats.capabilities.supportsPagination ? '✅' : '❌'}</div>
                <div>🔍 Búsqueda: {stats.capabilities.supportsSearch ? '✅' : '❌'}</div>
              </div>
            )}
            <div style={{marginTop: '8px', fontSize: '10px', opacity: 0.8, borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '5px'}}>
              🌐 Servidor: {stats.capabilities?.supportsPagination ? 'Moderno' : 'Legacy'}<br/>
              📅 Actualizado: {new Date().toLocaleDateString()}<br/>
              🔍 Consola: window.buscarArticulos('término')<br/>
              🛠️ Debug: window.debugAgustinos
            </div>
          </div>
        )}

      {currentView === 'main' && (
        <>
          <Header
            currentView={currentView}
            currentSection={currentSection}
            teInteresaArticles={allArticles['Te interesa'] || []}
            onArticleClick={handleArticleClick}
            onSectionClick={handleSectionClick}
          />
          
          {/* Navigation Pills */}
          <NavigationPills 
            currentView={currentView}
            onSectionClick={handleSectionClick}
            onClaveAgustinianaClick={handleClaveAgustinianaClick}
            allArticles={allArticles}
          />

          {/* Main Content with Pull-to-Refresh */}
          <PullToRefresh 
            onRefresh={handleRefresh}
            disabled={sectionsLoading}
          >
            <div style={{ paddingBottom: 'calc(100px + env(safe-area-inset-bottom, 0px))' }}>
            {(() => {
              // Mostrar información sobre limitaciones si existe
              // Desactivado para no mostrar advertencia al usuario
              const showLimitationWarning = false;
              
              return Object.keys(allArticles).length > 0 && !sectionsLoading ? (
                <>
                  {/* Mostrar advertencia sobre limitación si aplica */}
                  {showLimitationWarning && (
                    <div className="mx-5 mb-4 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl">
                      <div className="flex items-center mb-2">
                        <div className="text-yellow-600 text-lg mr-2">⚠️</div>
                        <h4 className="text-yellow-800 font-semibold text-sm">Contenido Limitado</h4>
                      </div>
                      <p className="text-yellow-700 text-xs leading-relaxed">
                        Mostrando <strong>{stats.capabilities.maxArticlesShown}</strong> artículos más recientes de <strong>{stats.capabilities.totalArticlesAvailable}</strong> disponibles en el servidor.
                      </p>
                      {!stats.capabilities.supportsPagination && (
                        <p className="text-yellow-600 text-xs mt-2 italic">
                          💡 Para acceder a todo el contenido, se requiere actualizar la API del backend.
                        </p>
                      )}
                    </div>
                  )}
                  
                  {orderedSections.map(([section, articles], sectionIndex) => {
                      const itemsFormatted = (articles || []).slice(0, 5).map(article => ({
                        title: article.title,
                        description: article.description || article.subtitle,
                        image: article.imageUrl || PLACEHOLDER_MINIATURA,
                        originalData: {
                          ...article,
                          date: article.date instanceof Date ? article.date.toISOString() : article.date,
                          id: article.id?.toString(),
                          title: article.title?.toString() || '',
                          description: (article.description || article.subtitle || '').toString()
                        }
                      }));

                      // Lógica específica para cada sección
                      if (section === 'Evangelio') {
                        // Primera sección: Evangelio (comportamiento original)
                        return (
                          <ContentSection
                            key={section}
                            title={section}
                            items={itemsFormatted}
                            onViewAll={(sectionTitle) => handleSectionClick(sectionTitle)}
                            onSelectItem={(item, sectionTitle) => {
                              handleArticleClick(item.originalData, sectionTitle);
                            }}
                            onAudioClick={handleAudioClick}
                          />
                        );
                      } else {
                        // Segunda sección: Dinámica (nueva lógica)
                        return (
                          <ContentSection
                            key={section}
                            title={section}
                            items={itemsFormatted}
                            onViewAll={async (sectionTitle) => {
                              const navigation = await getDailySectionNavigation(allArticles);
                              if (navigation === 'clave-agustiniana') {
                                handleClaveAgustinianaClick();
                              } else {
                                handleSectionClick(navigation);
                              }
                            }}
                            onSelectItem={(item, sectionTitle) => {
                              handleArticleClick(item.originalData, sectionTitle);
                            }}
                            onAudioClick={handleAudioClick}
                          />
                        );
                      }
                    })}
                </>
              ) : sectionsLoading ? (
                <div className="modern-card mx-5 text-center py-12">
                  <div className="section-icon mx-auto mb-4" style={{
                    background: 'linear-gradient(135deg, #e62c19 0%, #a01916 100%)'
                  }}>
                    🔄
                  </div>
                  <h3 className="text-lg font-semibold text-primary mb-2">
                    ⏳ Cargando secciones...
                  </h3>
                  <p className="text-secondary text-sm">
                    Verificando santos del día y organizando contenido
                  </p>
                </div>
              ) : (
                <div className="modern-card mx-5 text-center py-12">
                  <div className="section-icon mx-auto mb-4" style={{
                    background: 'linear-gradient(135deg, #e62c19 0%, #a01916 100%)'
                  }}>
                    🌐
                  </div>
                  <h3 className="text-lg font-semibold text-primary mb-2">
                    ⏳ Conectando con servidor...
                  </h3>
                  <p className="text-secondary text-sm">
                    Cargando contenido desde el servidor remoto
                  </p>
                  
                  {/* Mostrar advertencia sobre limitación si aplica */}
                  {showLimitationWarning && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="text-yellow-800 text-xs">
                        ⚠️ Mostrando {stats.capabilities.maxArticlesShown} artículos más recientes de {stats.capabilities.totalArticlesAvailable} disponibles
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Video Carousel Section - Videos de Buenos días */}
            {(() => {
              // Filtrar videos de la sección "Buenos días"
              const buenosDiasArticles = allArticles['Buenos días'] || [];
              const videoArticles = buenosDiasArticles
                .filter(article => article.hasVideo && article.videoUrl)
                .sort((a, b) => new Date(b.date) - new Date(a.date)) // Más recientes primero
                .slice(0, 5); // Limitar a 5 videos

              return videoArticles.length > 0 ? (
                <VideoCarousel
                  videoArticles={videoArticles}
                  title="Vídeos destacados"
                  onVideoClick={(article) => {
                    if (article === 'view-all') {
                      // Navegar a la sección de TODOS los vídeos
                      console.log('🎥 Navegando a sección Vídeos destacados (todos)');
                      handleSectionClick('Vídeos destacados');
                    } else {
                      // Abrir el artículo del video
                      handleArticleClick(article, 'Buenos días');
                    }
                  }}
                  onVideoModalClick={handleVideoModalClick}
                />
              ) : null;
            })()}

            {/* Camino de Santiago (acceso a sección especial, solo en temporada) */}
            {remoteConfig.mostrarCaminoHome && (
              <CaminoHomeCard
                etapasCount={(allArticles['Camino — Etapas'] || []).length}
                galeriasCount={(allArticles['Camino — Galería'] || []).length}
                mochilaCount={(allArticles['Camino — En mi mochila'] || []).length}
                onClick={(tab) => {
                  // Click en pill: usa esa pestaña. Click en el body de la card
                  // (sin tab explícita): abre "En mi mochila" como pestaña por defecto.
                  const target =
                    tab === 'etapas' ? 'etapas' :
                    tab === 'galeria' ? 'galeria' :
                    'mochila';
                  console.log('🎒 Navegando a Camino de Santiago, tab:', target);
                  setCaminoInitialTab(target);
                  setCurrentView('camino');
                  if (navigate) {
                    navigate(`/camino?tab=${target}`);
                  }
                }}
              />
            )}

            {/* Sección Mis Favoritos */}
            <FavoritesSection
              favoriteArticles={favoriteArticles}
              onViewAll={() => {
                console.log('⭐️ Navegando a Ver todos los favoritos');
                handleSectionClick('Mis favoritos');
              }}
              onSelectArticle={(article, sectionTitle) => {
                handleArticleClick(article, sectionTitle || 'Mis favoritos');
              }}
            />

            {/* Banner Formación Agustiniana */}
            <div 
              className="mx-5 mb-6 rounded-3xl overflow-hidden cursor-pointer hover:opacity-90 transition-opacity shadow-lg"
              onClick={() => window.open('https://formacionagustiniana.es', '_blank')}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  window.open('https://formacionagustiniana.es', '_blank');
                }
              }}
              aria-label="Visitar Formación Agustiniana"
            >
              <img 
                src="/assets/banner-formacion-agustiniana.jpg" 
                alt="Formación Agustiniana"
                className="w-full h-auto"
                style={{ 
                  display: 'block',
                  objectFit: 'contain',
                  maxHeight: '300px'
                }}
              />
            </div>
            
            {/* Contact Section */}
            <div 
              className="mx-5 mb-6 text-center py-8 px-6 text-white rounded-3xl"
              style={{
                background: 'linear-gradient(135deg, #e62c19 0%, #a01916 100%)'
              }}
            >
              <h2 className="text-xl font-bold mb-4">¿Inquietud vocacional?</h2>
              <button 
                className="bg-white text-red-600 px-8 py-3 rounded-full font-semibold transition-all hover:scale-105 hover:shadow-lg"
                onClick={() => window.location.href = 'mailto:comunicacion@agustinos.es?subject=Inquietud vocacional'}
              >
                CONTÁCTANOS
              </button>
            </div>

            {/* Footer */}
            <Footer />

            </div>
          </PullToRefresh>
        </>
      )}

      {currentView === 'list' && (
        <div style={{ paddingBottom: 'calc(100px + env(safe-area-inset-bottom, 0px))' }}>
          <ArticleListView
            sectionTitle={currentSection}
            initialFilter={listInitialFilter}
            articles={(allArticles[currentSection] || []).map(article => ({
              ...article,
              date: article.date instanceof Date ? article.date.toISOString() : article.date,
              id: article.id?.toString() || '',
              title: article.title?.toString() || '',
              subtitle: article.subtitle?.toString() || '',
              description: (article.description || article.subtitle || '').toString(),
              author: (article.author || '').toString(),
              // IMPORTANTE: Mapear correctamente la imagen
              image: article.imageUrl || article.image || PLACEHOLDER_MINIATURA,
              hasAudio: Boolean(article.hasAudio),
              audioUrl: article.audioUrl || ''
            }))}
            onBack={handleBackClick}
            onSelectArticle={(article) => {
              console.log('🎯 ArticleListView - Artículo seleccionado:', article.title);
              handleArticleClick(article, currentSection);
            }}
          />
        </div>
      )}

      {currentView === 'detail' && currentArticle && (
        <div style={{ paddingBottom: 'calc(100px + env(safe-area-inset-bottom, 0px))' }}>
          <ArticleDetailView
            article={{
              ...currentArticle,
              // Asegurar que date sea string
              date: currentArticle.date instanceof Date ? 
                    currentArticle.date.toISOString() : 
                    currentArticle.date
            }}
            sectionTitle={currentSection}
            onBack={handleBackClick}
            allArticles={allArticles}
            onArticleClick={handleArticleClick}
            onSectionClick={handleSectionClick}
          />
        </div>
      )}

      {currentView === 'social' && (
        <div style={{ paddingBottom: 'calc(100px + env(safe-area-inset-bottom, 0px))' }}>
          <Header 
            currentView={currentView}
            currentSection={currentSection}
            teInteresaArticles={allArticles['Te interesa'] || []}
          />
          <SocialMediaView />
        </div>
      )}

      {currentView === 'ven-y-veras' && (
        <div style={{ paddingBottom: 'calc(100px + env(safe-area-inset-bottom, 0px))' }}>
          <Header 
            currentView={currentView}
            currentSection={currentSection}
            teInteresaArticles={allArticles['Te interesa'] || []}
          />
          <VenYVerasView onBack={handleBackClick} />
        </div>
      )}

      {currentView === 'clave-agustiniana' && (
        <div style={{ paddingBottom: 'calc(100px + env(safe-area-inset-bottom, 0px))' }}>
          <ClaveAgustinianaView
            onBack={handleBackClick}
            onSectionClick={(section, options) => {  // ← AGREGAR options
              setCurrentView('main');
              handleSectionClick(section, options);  // ← PASAR options
            }}
          />
        </div>
      )}

      {currentView === 'camino' && (
        <CaminoView
          allArticles={allArticles}
          onBack={handleBackClick}
          onSelectArticle={(article, section) => handleArticleClick(article, section)}
          initialTab={caminoInitialTab}
        />
      )}

      {showCalendar && (
        <FullCalendar onClose={() => {
          console.log('🗓️ App.js - Cerrando FullCalendar');
          setShowCalendar(false);
        }} />
      )}

      {/* Bottom Navigation */}
      <BottomNavigation 
        currentView={currentView}
        currentSection={currentSection}
        onHomeClick={handleHomeClick}
        onSectionClick={handleSectionClick}
        onCalendarClick={() => {
          console.log('🗓️ Abriendo FullCalendar desde BottomNavigation');
          setShowCalendar(true);
          // No navegamos a ninguna URL, solo abrimos el popup
        }}
        onSocialMediaClick={handleSocialMediaClick}
      />

      {/* Audio Modal */}
      <AudioModal
        isOpen={audioModal.isOpen}
        onClose={handleCloseAudioModal}
        article={audioModal.article}
        sectionTitle={audioModal.sectionTitle}
      />

      {/* Video Modal */}
      <VideoModal
        isOpen={videoModal.isOpen}
        onClose={handleCloseVideoModal}
        article={videoModal.article}
        sectionTitle={videoModal.sectionTitle}
      />
      </div>
    </div>
  );
}

// Componente principal con Router
function App() {
  // ===== ESTADO GLOBAL (se carga una sola vez) =====
  const [appReady, setAppReady] = useState(false);
  const [allArticles, setAllArticles] = useState({});
  const [dataLoader, setDataLoader] = useState(null);
  const [stats, setStats] = useState(null);
  const [orderedSections, setOrderedSections] = useState([]);
  const [sectionsLoading, setSectionsLoading] = useState(true);

  // ===== HANDLER PARA CARGA DE DATOS (se ejecuta una sola vez) =====
  const handleDataLoaded = useCallback(({ articles, loader, stats: serverStats, pagination, capabilities, hasMore, source, isLimited }) => {
    console.log('🔍 DEBUG: handleDataLoaded ejecutándose en App.js');
    
    logAPI('Datos del servidor cargados exitosamente!');
    devLog('Estadísticas del servidor:', serverStats);
    devLog('Capacidades del backend:', capabilities);
    
    // Guardar datos en estado global
    setAllArticles(articles);
    setDataLoader(loader);
    
    // Proteger contra capabilities undefined (modo offline)
    const safeCapabilities = capabilities || { supportsPagination: false };
    
    const finalStats = {
      totalArticulos: Object.values(articles).reduce((sum, arr) => sum + (arr?.length || 0), 0),
      conAudio: Object.values(articles).flat().filter(a => a.hasAudio).length,
      secciones: Object.keys(articles).length,
      fechaReciente: new Date(),
      fechaAntigua: new Date(2022, 3, 1),
      archivosRango: safeCapabilities.supportsPagination ? 'Servidor moderno' : 'Servidor legacy (limitado)',
      ...serverStats,
      capabilities: safeCapabilities,
      pagination,
      hasMore,
      source,
      isLimited
    };
    
    setStats(finalStats);
    
    // Exponer datos globalmente
    window.agustinosAppData = { 
      articles, 
      sections: articles,
      stats: finalStats, 
      capabilities, 
      pagination,
      loader 
    };
    
    window.testSantoDelDia = testSantoDelDia;
    window.debugSantosPrioridad = debugSantosPrioridad;
    
    // Marcar app como lista
    setAppReady(true);
    console.log('✅ App lista para usar');
  }, []);

  // ===== GENERAR SECCIONES ORDENADAS =====
  useEffect(() => {
    async function generateOrderedSections() {
      const sections = [];
      
      // Primera sección: Evangelio
      if (allArticles['Evangelio'] && allArticles['Evangelio'].length > 0) {
        sections.push(['Evangelio', allArticles['Evangelio']]);
      }
      
      // Segunda sección: Dinámica según día de la semana
      const dailySectionTitle = await getDailySectionTitle(allArticles);
      const dailySectionArticles = await getDailySectionArticles(allArticles);
      
      if (dailySectionArticles && dailySectionArticles.length > 0) {
        sections.push([dailySectionTitle, dailySectionArticles]);
      }
      
      return sections;
    }
    
    if (appReady && Object.keys(allArticles).length > 0) {
      generateOrderedSections().then(sections => {
        setOrderedSections(sections);
        setSectionsLoading(false);
        logSections('Orden de secciones aplicado:', sections.map(([s]) => s));
      });
    }
  }, [appReady, allArticles]);

  // ===== INICIALIZAR PUSH NOTIFICATIONS Y STATUS BAR =====
  useEffect(() => {
    if (appReady) {
      // Inicializar push notifications solo en apps nativas
      pushNotificationService.initialize().catch(error => {
        console.error('❌ Error inicializando push notifications:', error);
      });

      // Configurar Status Bar en apps nativas
      if (Capacitor.isNativePlatform()) {
        StatusBar.setStyle({ style: Style.Light }).catch(err => {
          console.warn('⚠️ Error configurando estilo de Status Bar:', err);
        });
        StatusBar.setOverlaysWebView({ overlay: false }).catch(err => {
          console.warn('⚠️ Error configurando overlay de Status Bar:', err);
        });
        StatusBar.setBackgroundColor({ color: '#c82318' }).catch(err => {
          console.warn('⚠️ Error configurando color de Status Bar:', err);
        });
        console.log('✅ Status Bar configurado');
      }
    }
  }, [appReady]);

  // ===== CALLBACK PARA ACTUALIZAR ARTÍCULOS (usado por pull-to-refresh) =====
  const updateArticles = useCallback((newArticles) => {
    setAllArticles(newArticles);
    setSectionsLoading(true); // Re-generar secciones
  }, []);

  // ===== MOSTRAR LOADING SCREEN SOLO EN CARGA INICIAL =====
  if (!appReady) {
    return <ApiTestDataLoader onDataLoaded={handleDataLoaded} />;
  }

  // ===== PROPS COMPARTIDAS PARA TODAS LAS RUTAS =====
  const sharedProps = {
    allArticles,
    dataLoader,
    stats,
    orderedSections,
    sectionsLoading,
    onUpdateArticles: updateArticles
  };

  // ===== ROUTER (solo se monta cuando app está lista) =====
  return (
    <Router>
      <Routes>
        {/* Página principal */}
        <Route path="/" element={<AppContent {...sharedProps} />} />
        
        {/* Artículo específico: /evangelio/123/titulo-del-articulo */}
        <Route path="/:seccion/:id/:slug" element={<ArticleRoute {...sharedProps} />} />
        
        {/* Vista de sección: /seccion/evangelio */}
        <Route path="/seccion/:nombre" element={<SectionRoute {...sharedProps} />} />
        
        {/* Clave agustiniana */}
        <Route path="/clave-agustiniana" element={<ClaveAgustinianaRoute {...sharedProps} />} />

        {/* Camino de Santiago */}
        <Route path="/camino" element={<CaminoRoute {...sharedProps} />} />
        
        {/* Calendario */}
        <Route path="/calendario" element={<CalendarRoute {...sharedProps} />} />
        
        {/* Media/Social */}
        <Route path="/media" element={<MediaRoute {...sharedProps} />} />
        
        {/* Términos y Privacidad */}
        <Route path="/terminos-y-condiciones" element={<TermsView />} />
        <Route path="/politica-de-privacidad" element={<PrivacyView />} />
        
        {/* Configuración, Créditos y Notificaciones */}
        <Route path="/configuracion" element={<ConfiguracionView />} />
        <Route path="/creditos" element={<CreditosView />} />
        <Route path="/notificaciones" element={<NotificationsHistoryView />} />
        
        {/* Fallback a página principal */}
        <Route path="*" element={<AppContent {...sharedProps} />} />
      </Routes>
    </Router>
  );
}

// Función para convertir slug de URL a nombre real de sección
const slugToSectionName = (slug) => {
  const sectionMapping = {
    'evangelio': 'Evangelio',
    'buenos-dias': 'Buenos días',
    'buenos-días': 'Buenos días', // Con tilde también
    'en-clave-agustiniana': 'En clave agustiniana',
    'santos-agustinos': 'Santos Agustinos',
    'vocaciones': 'Vocaciones',
    'vamos-al-cole': 'Vamos al cole',
    'te-interesa': 'Te interesa',
    'esta-pasando': 'Está pasando',
    // Secciones que ahora son reales en el backend
    'pildoras-de-san-agustin': 'Píldoras de San Agustín',
    'habla-san-agustin': 'Habla San Agustín',
    'orando-con-san-agustin': 'Orando con San Agustín',
    'historia-y-sabias-que': 'Historia y personajes + Sabías que…',
    'reflexion-agustiniana': 'Reflexión agustiniana',
    'domingo-con-san-agustin': 'Domingo con San Agustín',
    'sabias-que': 'Sabías que…',
    // Sección virtual de vídeos
    'videos-destacados': 'Vídeos destacados',
    // Sección virtual de favoritos
    'mis-favoritos': 'Mis favoritos',
    // Camino de Santiago: secciones reales + slug "padre" para la pantalla.
    'camino': 'Camino de Santiago',
    'camino-etapas': 'Camino — Etapas',
    'camino-galeria': 'Camino — Galería',
    'camino-en-mi-mochila': 'Camino — En mi mochila',
    'camino-brujula': 'Camino — Brújula'
  };
  
  // Si existe un mapeo específico, usarlo
  if (sectionMapping[slug]) {
    return sectionMapping[slug];
  }
  
  // Si no existe mapeo, convertir el slug a título capitalizado
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Componente para rutas de artículos
function ArticleRoute(props) {
  const { seccion, id } = useParams();
  
  // Convertir slug a nombre real de sección
  const realSectionName = slugToSectionName(seccion);
  
  return (
    <AppContent 
      {...props}
      initialView="detail"
      initialArticleId={id}
      initialSection={realSectionName}
    />
  );
}

// Componente para rutas de sección
function SectionRoute(props) {
  const { nombre } = useParams();
  const [searchParams] = useSearchParams();
  const filtro = searchParams.get('filtro');
  
  // Usar la misma función para convertir slug a nombre de sección
  let realSectionName = slugToSectionName(nombre);
  
  // Casos especiales para secciones múltiples
  if (nombre === 'historia-y-sabias-que') {
    realSectionName = 'Historia y personajes + Sabías que…';
  }
  
  // Caso especial para sección virtual de favoritos
  if (nombre === 'mis-favoritos') {
    realSectionName = 'Mis favoritos';
  }

  // Caso especial para sección virtual de videos
  if (nombre === 'videos-destacados') {
    // Crear la sección virtual al inicializar desde URL
    const buenosDiasArticles = props.allArticles['Buenos días'] || [];
    const todosLosVideos = buenosDiasArticles
      .filter(article => article.hasVideo && article.videoUrl)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    
    if (todosLosVideos.length > 0 && !props.allArticles['Vídeos destacados']) {
      props.onUpdateArticles({
        ...props.allArticles,
        'Vídeos destacados': todosLosVideos
      });
    }
  }

  return (
    <AppContent
      {...props}
      initialView="list"
      initialSection={realSectionName}
      initialListFilter={filtro}
    />
  );
}

// Componente para ruta de calendario
function CalendarRoute(props) {
  const navigate = useNavigate();
  
  // Redirigir a inicio y abrir calendario como popup
  useEffect(() => {
    navigate('/');
    // Pequeño delay para asegurar que la navegación se complete
    setTimeout(() => {
      // Disparar evento para abrir calendario
      const openCalendarEvent = new CustomEvent('openCalendarPopup');
      window.dispatchEvent(openCalendarEvent);
    }, 100);
  }, [navigate]);

  return (
    <AppContent {...props} />
  );
}

// Componente para ruta de media
function MediaRoute(props) {
  return (
    <AppContent 
      {...props}
      initialView="social"
    />
  );
}

function ClaveAgustinianaRoute(props) {
  return (
    <AppContent
      {...props}
      initialView="clave-agustiniana"
    />
  );
}

function CaminoRoute(props) {
  const [searchParams] = useSearchParams();
  const raw = searchParams.get('tab');
  const tab = raw === 'galeria' ? 'galeria' : raw === 'mochila' ? 'mochila' : 'etapas';
  return (
    <AppContent
      {...props}
      initialView="camino"
      initialCaminoTab={tab}
    />
  );
}

export default App;